import type { ProductCenterProductsResponse } from "@adpilot/shared";
import type {
  PerformanceKpi,
  PerformanceTrendPoint,
} from "@/components/shared/performance/performance";
import {
  type ScopeAccountMix,
  type SpApiMetric,
  scopeMetricAvailability,
} from "@/lib/vendor-metric-availability";

export const PRODUCT_CENTER_METRICS = [
  "totalSales",
  "adSales",
  "units",
  "spend",
  "orders",
  "acos",
  "tacos",
  "sessions",
] as const;
export type ProductCenterMetric = (typeof PRODUCT_CENTER_METRICS)[number];
export const PRODUCT_CENTER_DEFAULT_METRICS: ProductCenterMetric[] = [
  "totalSales",
  "adSales",
  "units",
  "spend",
  "tacos",
  "acos",
];

/** SP-API (Seller / Vendor Central) source of each seller-side Product Center metric. */
const SP_API_METRIC: Partial<Record<ProductCenterMetric, SpApiMetric>> = {
  totalSales: "totalSales",
  units: "totalOrders",
  tacos: "tacos",
  sessions: "sessions",
};

/**
 * KPI menu for a scope: a vendor-only scope drops the metrics Amazon never
 * reports for vendors (sessions), so the menu never promises them (#675).
 */
export function productCenterMetricsFor(mix: ScopeAccountMix): ProductCenterMetric[] {
  return PRODUCT_CENTER_METRICS.filter((key) => {
    const source = SP_API_METRIC[key];
    return !source || scopeMetricAvailability(mix, source) !== "never";
  });
}

export function buildProductPerformance(
  data: ProductCenterProductsResponse | undefined,
  t: (key: string) => string,
  mix: ScopeAccountMix = "seller",
): {
  kpis: PerformanceKpi<ProductCenterMetric>[];
  trend: PerformanceTrendPoint<ProductCenterMetric>[];
} {
  if (!data) return { kpis: [], trend: [] };
  const metrics = productCenterMetricsFor(mix);
  const vendorPending = (key: ProductCenterMetric) => {
    const source = SP_API_METRIC[key];
    return source != null && scopeMetricAvailability(mix, source) === "pending_vendor_central";
  };
  const sellerDescription = t("performance.sellerUnavailable");
  const adsAvailable = data.summary.ads.status !== "unavailable";
  const tacosEligible = data.tacosEligibility.status === "available";
  const values: Record<ProductCenterMetric, number | null> = {
    totalSales: data.summary.seller.sales,
    adSales: adsAvailable ? data.summary.ads.adSales : null,
    units: data.summary.seller.units,
    spend: adsAvailable ? data.summary.ads.spend : null,
    orders: adsAvailable ? data.summary.ads.orders : null,
    acos: adsAvailable ? data.summary.ads.acos : null,
    tacos:
      tacosEligible && data.summary.seller.status === "available" && data.summary.seller.sales > 0
        ? data.summary.ads.spend / data.summary.seller.sales
        : null,
    sessions: data.summary.seller.sessions,
  };
  const formats: Record<ProductCenterMetric, "currency" | "number" | "percent"> = {
    totalSales: "currency",
    adSales: "currency",
    units: "number",
    spend: "currency",
    orders: "number",
    acos: "percent",
    tacos: "percent",
    sessions: "number",
  };
  const kpis = metrics.map((key) => ({
    key,
    label: t(`performance.metrics.${key}`),
    value: values[key],
    previousValue: null,
    change: null,
    direction: "flat" as const,
    semantics: "neutral" as const,
    format: formats[key],
    currency:
      key === "totalSales" || key === "adSales" || key === "spend" ? data.currency : undefined,
    // Vendor (1P) shops get these from Vendor Central (P1); say so instead of
    // pointing at a Seller Central connection they can never make.
    description: vendorPending(key)
      ? t("common:vendorData.pendingTitle")
      : key === "tacos" && data.tacosEligibility.status === "unavailable"
        ? t(`performance.tacosUnavailable.${data.tacosEligibility.reason}`)
        : (key === "adSales" || key === "spend" || key === "orders" || key === "acos") &&
            data.summary.ads.status !== "available"
          ? t(
              data.summary.ads.status === "partial"
                ? "performance.adsPartialValue"
                : "performance.adsUnavailable",
            )
          : data.summary.seller.status === "unavailable" &&
              (key === "totalSales" || key === "units" || key === "tacos" || key === "sessions")
            ? sellerDescription
            : undefined,
  }));
  const trend = data.trend.map((point) => {
    const metricDataStatus: Partial<
      Record<ProductCenterMetric, "complete" | "partial" | "missing">
    > = {};
    if (point.ads.status !== "available") {
      const status = point.ads.status === "partial" ? "partial" : "missing";
      for (const key of ["adSales", "spend", "orders", "acos"] as const)
        metricDataStatus[key] = status;
    }
    if (point.seller.status !== "available") {
      const status = point.seller.status === "partial" ? "partial" : "missing";
      for (const key of ["totalSales", "units", "tacos", "sessions"] as const)
        metricDataStatus[key] = status;
    }
    return {
      periodStart: point.date,
      periodEnd: point.date,
      dataStatus: "complete" as const,
      totalSales: point.seller.sales,
      adSales: point.ads.status === "unavailable" ? null : point.ads.adSales,
      units: point.seller.units,
      spend: point.ads.status === "unavailable" ? null : point.ads.spend,
      orders: point.ads.status === "unavailable" ? null : point.ads.orders,
      acos: point.ads.status === "unavailable" ? null : point.ads.acos,
      tacos:
        tacosEligible && point.seller.status === "available" && point.seller.sales > 0
          ? point.ads.spend / point.seller.sales
          : null,
      sessions: point.seller.sessions,
      metricDataStatus: Object.keys(metricDataStatus).length > 0 ? metricDataStatus : undefined,
    };
  });
  return { kpis, trend };
}
